import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { theme } from 'lib/theme';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  doReportingGetReportCsv,
  doReportingMergeDraftFilterValues,
  doReportingShowReportFilterModal,
  doReportingSetReportVisualsSize,
  doReportingGetReportListData,
} from 'store/actions';
import {
  selectRptReportFilterOptions,
  selectRptReportFilterValues,
  selectRptReportHasVisuals,
  selectRptReportName,
  selectRptReportTotalCount,
  selectRptReportVisualsSize,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import { IconButton } from 'components/IconButton';
import { QuickOptionField } from 'components/Field/QuickOptionField';
import { LayoutChangeEvent } from 'react-native';
import { smartText } from 'lib/str';
import { ReportingVisualsSizeEnum } from 'types/types';
import { PageHeader } from 'components/PageHeader';
import { ButtonGroup } from 'components/ButtonGroup';
import { getBreakpoint } from 'hooks/useScreenBreakpoints';
import { PageToolbar } from 'components/PageToolbar';

type Report = ReportTypes[ReportEnum.elements];

const screenBreakpoints = [
  {
    width: 400,
    key: 1,
  },
  {
    width: 450,
    key: 2,
  },
  {
    width: 550,
    key: 3,
  },
  {
    width: 700,
    key: 4,
  },
  {
    width: 850,
    key: 5,
  },
];

export const Header: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const visualsSize = useSelector(selectRptReportVisualsSize);
  const hasVisuals = useSelector((state) =>
    selectRptReportHasVisuals(state, reportKey),
  );
  const reportName = useSelector((state) =>
    selectRptReportName(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );

  const filterValues = useSelector((state) =>
    selectRptReportFilterValues(state, reportKey),
  )! as Report['filterValues'];
  const filterOptions = useSelector((state) =>
    selectRptReportFilterOptions(state, reportKey),
  )! as Report['filterOptions'];

  const handleLoadReportCsv = React.useCallback(() => {
    dispatch(
      doReportingGetReportCsv({
        key: reportKey,
      }),
    );
  }, [dispatch, reportKey]);

  const handleFilterChange = React.useCallback(
    (mergeFilterValues: Partial<Report['filterValues']>) => {
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: mergeFilterValues,
          loadData: true,
        }),
      );
    },
    [dispatch, reportKey],
  );

  // Search field
  const handleSearchChange = React.useCallback(
    (newSearchValue: string) =>
      dispatch(
        doReportingMergeDraftFilterValues({
          key: reportKey,
          filterValues: { search: newSearchValue },
          loadData: true,
        }),
      ),
    [dispatch, reportKey],
  );
  const filteredOrganisations = filterValues.organisationIds.length
    ? filterOptions.organisations.filter((item) =>
        filterValues.organisationIds.includes(item.value),
      )
    : filterOptions.organisations.length === 1
      ? filterOptions.organisations
      : [];

  const [breakpoint, setBreakpoint] = React.useState<number>(1);
  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) =>
      setBreakpoint(
        getBreakpoint(screenBreakpoints, event.nativeEvent.layout.width) || 1,
      ),
    [],
  );

  return (
    <>
      <PageHeader
        title={reportName}
        subtitle={
          pluralise(
            reportTotalCount,
            '{x} Result',
            '{x} Results',
            'No Results',
          ) +
          (filteredOrganisations.length > 1
            ? ` • ${filteredOrganisations.length} Organisations`
            : filteredOrganisations.length === 1
              ? ` • ${filteredOrganisations[0]!.name}`
              : ' • All Organisations')
        }
        showSidebarButton
        menuRight={{
          iconButtonConfig: { icon: ICONS.EllipsisLight },
          options: [
            {
              text: 'Export List as CSV',
              icon: ICONS.ArrowDownLight,
              onPress: handleLoadReportCsv,
            },
            <CardRow
              key="visualsOptionsRow"
              label="Visuals Options"
              disabled={!hasVisuals}
              navigationIcon={ICONS.ChartBarDuotone}
              navigationIconColor={theme.colors.black}
              style={{
                width: 250,
                minHeight: 44,
                backgroundColor: '#fafafa',
              }}
              hoveredStyle={{ backgroundColor: '#f1f1f1' }}
              childrenStyle={{}}
              menuOptions={
                hasVisuals
                  ? [
                      {
                        text: 'Large Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.large
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.large,
                            }),
                          ),
                      },
                      {
                        text: 'Small Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.small
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.small,
                            }),
                          ),
                      },
                      {
                        text: 'Hide Visuals',
                        icon:
                          visualsSize === ReportingVisualsSizeEnum.none
                            ? ICONS.CheckLight
                            : undefined,
                        onPress: () =>
                          dispatch(
                            doReportingSetReportVisualsSize({
                              size: ReportingVisualsSizeEnum.none,
                            }),
                          ),
                      },
                      smartText(
                        'Some reports include visuals like charts and graphs. They only appear if they have data to show',
                      ),
                    ]
                  : undefined
              }
              showMenuOnPress
              hideMenuButton
            />,
          ],
        }}
      />
      <PageToolbar
        onLayout={handleLayout}
        onSearch={handleSearchChange}
        searchValue={filterValues.search}
        searchPlaceholder={`Search ${reportName}`}
        searchOnSubmit
      >
        <ButtonGroup>
          {breakpoint > 1 && (
            <QuickOptionField
              labelNone="All Elements"
              labelAll="All Elements"
              labelMultiple="{x} Elements"
              modalTitle="Elements"
              options={filterOptions.elements.map((item) => ({
                name: item.name,
                value: item.value,
              }))}
              values={filterValues.elements}
              enableSelectAll
              buttonMaxWidth={150}
              onChange={(newValue) =>
                handleFilterChange({ elements: newValue })
              }
              modal
              modalSize="small"
              modalSearchable
              multiSelect
            />
          )}
          {breakpoint > 2 && (
            <QuickOptionField
              labelNone="Tasks"
              labelAll="Tasks"
              labelMultiple="{x} Tasks"
              modalTitle="Task Statuses"
              options={filterOptions.taskStatuses.map((item) => ({
                name: item.name,
                value: item.value,
                icon: ICONS.CircleSolid,
                iconColor: `#${item.color}`,
              }))}
              noValueOption={{
                name: 'All / None',
                subname: 'Task Statuses',
              }}
              values={filterValues.taskStatusesWith}
              enableSelectAll
              buttonMaxWidth={150}
              onChange={(newValue) =>
                handleFilterChange({ taskStatusesWith: newValue })
              }
            />
          )}
          {breakpoint > 3 && (
            <QuickOptionField
              labelNone="Checks"
              labelAll="Checks"
              labelMultiple="{x} Checks"
              modalTitle="Check Statuses"
              options={filterOptions.checkStatuses.map((item) => ({
                name: item.name,
                value: item.value,
                icon: ICONS.CircleSolid,
                iconColor: `#${item.color}`,
              }))}
              noValueOption={{
                name: 'All / None',
                subname: 'Check Statuses',
              }}
              values={filterValues.checkStatuses}
              enableSelectAll
              buttonMaxWidth={150}
              onChange={(newValue) =>
                handleFilterChange({ checkStatuses: newValue })
              }
            />
          )}
          {breakpoint > 4 && (
            <QuickOptionField
              labelNone="Ch/Schedules"
              labelAll="Ch/Schedules"
              labelMultiple="{x} Ch/Schedules"
              modalTitle="Check Schedule Statuses"
              options={filterOptions.checkScheduleStatuses.map((item) => ({
                name: item.name,
                value: item.value,
                icon: ICONS.CircleSolid,
                iconColor: `#${item.color}`,
              }))}
              noValueOption={{
                name: 'All / None',
                subname: 'Check Schedule Statuses',
              }}
              values={filterValues.checkScheduleStatuses}
              enableSelectAll
              onChange={(newValue) =>
                handleFilterChange({ checkScheduleStatuses: newValue })
              }
            />
          )}
          <IconButton
            icon={ICONS.RotateRightLight}
            onPress={() =>
              dispatch(
                doReportingGetReportListData({
                  key: reportKey,
                  nextPage: false,
                  closeModals: false,
                  overlayLoader: true,
                }),
              )
            }
          />
          <IconButton
            icon={ICONS.SlidersUpLight}
            onPress={() =>
              dispatch(doReportingShowReportFilterModal({ key: reportKey }))
            }
            showBadge={
              !!filterValues.elements.length ||
              !!filterValues.organisationIds.length ||
              !!filterValues.checkStatuses.length ||
              !!filterValues.checkScheduleStatuses.length ||
              !!filterValues.taskStatusesWith.length ||
              !!filterValues.taskStatusesWithout.length
            }
          />
        </ButtonGroup>
      </PageToolbar>
    </>
  );
};
